/* eslint-disable @typescript-eslint/restrict-plus-operands */
/* eslint-disable security/detect-object-injection */
import * as leafletGeometryUtil from 'leaflet-geometryutil';
import { Injectable } from '@angular/core';
import { featureGroup, LatLngBounds, Map, Polygon, polygon as leafletPolygon, LatLngExpression, LatLng } from 'leaflet';
import { catchError, of, tap, Subscription, combineLatest, mergeMap, filter } from 'rxjs';
import { ZonesService } from '@app/modules/zones/services/zones.service';
import { Zone, GeometryTypes, GTCxFeature, GTCxLabel } from '../zones.model';
import { DataForZones } from '@app/services/data-for-zones.service';
import { area, polygon as turfPolygon } from '@turf/turf';
import { ResourceLoadState } from '@app/store/filters/models/resource-load.state';
import { DataDogService } from '@app/services/data-dog.service';

declare let L;

@Injectable({
  providedIn: 'root'
})
export class LeafletZoneService {
  QUADRANT_PIXEL_DISTANCE = 20;
  LABEL_LIMIT_ON_ZOOM = 5;
  // number of zones we want to see inside the currentMapBounds. prevents densely packed labels clustered on top of each other
  LABEL_LIMIT = 30;
  // arbitary area in square meters to determine that the zone is a HUGE zone that can be viewed from a zoomed out worldview, likely a big state or province that the customer has chosen
  SQUARE_METERS = 1000000000;
  WORLD_VIEW_ZOOM = 3;
  features: GTCxFeature[] = [];
  // noted as a default color in https://zonarsystems.atlassian.net/browse/ZTT-2956
  color = '#2C2C8B';
  zoneFeatureGroup = featureGroup([]);
  addZonesSubscription: Subscription = of().subscribe();
  labelLayers: GTCxLabel[] = [];
  visibleLabels: GTCxLabel[] = [];

  nClosestLayers = (leafletGeometryUtil as any).nClosestLayers;

  constructor(
    private zonesService: ZonesService,
    private dataForZones: DataForZones,
    private datadogService: DataDogService
  ) {}

  setFeatures(zones: Zone[]) {
    const features = zones.map(z => {
      // not everything in prod data is defined, so we have to do all these "?" checks

      return {
        type: 'Feature',
        id: z?.id,
        properties: {
          name: z?.name,
          categoryId: z?.categoryId,
          color: z.category?.color || this.color,
          categoryName: z.category?.name,
          id: z?.id,
          visible: z.category.visible
        },
        geometry: z?.geometry.features
      };
    });

    // because the Entity dev data so far has 2 geometries per one zone (1 point and 1 polygon) we have to loop through this again to make one geometry per one feature

    const modifiedFeatures = [];
    features.forEach(f => {
      f.geometry.forEach(g => {
        const modifiedFeature = {
          ...f,
          geometry: {
            ...g.geometry
          }
        };

        // per Sean from design, we do not anticipate that real customers would have a zone that is only one lat/long point. Dev data does return points, so no need to work with them in case they are oddly included
        if (modifiedFeature.geometry.type !== GeometryTypes.POINT) {
          modifiedFeatures.push(this.reverseLongLats(modifiedFeature));
        }
      });
    });

    this.features = modifiedFeatures;
  }

  addZonesToMap(map: Map) {
    const logMsg = 'ZONES_CHECKBOX_DEBUG: #addZonesToMap fired';
    this.displayConsoleLog(logMsg);

    // we need company id and for assets to finish first before loading zones per https://zonarsystems.atlassian.net/browse/ZTT-3177
    const waitForAuxiliaryData = combineLatest([this.dataForZones.getCurrentCompanyId()]);

    this.addZonesSubscription = waitForAuxiliaryData
      .pipe(
        tap(([companyId]) => {
          // resets to clean slate if a multicompany/zonar user has switched companies

          const logMsg = `ZONES_CHECKBOX_DEBUG: #addZonesToMap and companyId ${companyId} loaded`;
          this.displayConsoleLog(logMsg);

          this.zoneFeatureGroup.clearLayers();
          this.labelLayers = [];
          this.visibleLabels = [];
        }),
        mergeMap(([companyId]) => {
          const logMsg = 'ZONES_CHECKBOX_DEBUG: #addZonesToMap inside mergeMap';
          this.displayConsoleLog(logMsg);

          return this.zonesService.getZonesWithCategories(companyId as string).pipe(
            catchError(error => {
              // we still want to console.log here because if the error is on the client side after a successful HTTP request, the error otherwise gets swallowed up and never makes it to global error handler
              const logMsg = `ZONES_CHECKBOX_DEBUG: #addZonesToMap #getZonesWithCategories error: ${error}`;
              console.error(logMsg);
              this.datadogService.logError(logMsg, error);

              // we still want to return empty array in case of any error to have a smooth front end flow if a zonar or multicompany user switches companies from Comp A to Comp B. If there is a Zones API error on that switch, the current zones should always go away
              return of([]);
            })
          );
        })
      )
      .subscribe(zones => {
        if (!zones.length) {
          return;
        }

        const logMsg = 'ZONES_CHECKBOX_DEBUG: received zones';
        this.displayConsoleLog(logMsg);

        this.setFeatures(zones as Zone[]);
        map.addLayer(this.zoneFeatureGroup);
        this.features.forEach((f, index) => {
          if (f.properties.visible) {
            const color = f.properties.color;
            const polygon = leafletPolygon(
              f.geometry.coordinates as LatLngExpression[] | LatLngExpression[][] | LatLngExpression[][][],
              { color, weight: 1, fillOpacity: 0 }
            );
            const polygonLayer = polygon.addTo(map) as Polygon;
            const latLong = polygon.getBounds().getCenter();
            let _area: number;
            try {
              _area = this.getZoneArea(f.geometry.coordinates);
            } catch {
              _area = 0;
            }

            this.zoneFeatureGroup.addLayer(polygonLayer);

            (polygonLayer as any).id = f.properties.id;
            (polygonLayer as any).name = f.properties.name;
            (polygonLayer as any).propeties = f.properties;
            (polygonLayer as any).coordinates = f.geometry.coordinates;

            // add'l CSS for this found in src/app/modules/shared/components/map/map.theme.scss
            const icon = L.divIcon({
              className: 'gtcx-zone-name',
              html: `<label style="color: ${color}" id=${f.properties.id}>${f.properties.name}</label>`
            });
            const labelLayer = L.marker(latLong, { icon }) as GTCxLabel;

            labelLayer.zoneArea = _area;
            labelLayer.name = f.properties.name;
            labelLayer.id = f.properties.id;
            labelLayer.properties = f.properties;
            labelLayer.latLong = [latLong.lat, latLong.lng];

            const ids = this.labelLayers.map(label => label.id);

            if (!ids.includes(labelLayer.id)) {
              this.labelLayers = [...this.labelLayers, labelLayer];
            }
          }
        });

        const length = this.labelLayers.length;
        if (length) {
          this.labelLayers = this.sortByZoneAreaDesc(this.labelLayers);

          this.setLabelCollision(this.getCurrentMapBounds(map), map.getZoom(), map);

          // adjusts zone labels on pan (moveend) and zoom events
          this.onMapMoveEvent(map, 'moveend');
          this.onMapMoveEvent(map, 'zoom');
        }
      });
  }

  sortByZoneAreaDesc(labels: GTCxLabel[]): GTCxLabel[] {
    return labels.sort((a, b) => (a.zoneArea > b.zoneArea ? -1 : 1));
  }

  getZoneArea(coordinates): number {
    // linter rule disabled because it kept flagging this as unsafe but linter would not accept the proper type from the turfjs library even though this works
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    return area(turfPolygon([coordinates] as any));
  }

  setLabelCollision(currentMapBounds: LatLngBounds, zoom: number, map: Map) {
    // only grab labels visible in the currentMapBounds
    this.visibleLabels = this.labelLayers.filter(label => currentMapBounds.contains(label.getLatLng() as LatLng));

    if (this.visibleLabels.length <= this.LABEL_LIMIT_ON_ZOOM) {
      this.showNLabels(this.visibleLabels.length);
      return;
    }

    // if we have zoomed out far enough for a worldview
    if (zoom <= this.WORLD_VIEW_ZOOM) {
      this.showNLabels(1, this.visibleLabels);
      return;
    }

    // else, just show max "this.LABEL_LIMIT" zones per zoom/pan
    this.showNLabels(this.LABEL_LIMIT, this.visibleLabels);

    // logic to attempt label collision detection, not perfect, planned for refactor in q1 2024

    // get center latlong of map
    const centerLatLong = map.getCenter();

    // get closet label to the center
    const centerLabel: GTCxLabel = this.getClosetLayers(map, centerLatLong, 1)[0];

    if (centerLabel) {
      const xCenter = centerLabel.x;
      const yCenter = centerLabel.y;

      const closestLabelsToCenter = this.getClosetLayers(map, centerLabel.getLatLng() as LatLng);

      // for some reason, the x/y coordinates do not match up with a traditional 4 quadrant xy plane, i.e. one would think quad1's conditions would make it the NE section, but it was actually reversed around or the diagonal quadrant or something. This makes this overly confusing to think  in terms of cardinal directions, so they are just arbitrary quadrants

      const quad1 = closestLabelsToCenter.filter(label => label.x > xCenter && label.y > yCenter);
      const quad2 = closestLabelsToCenter.filter(label => label.x > xCenter && label.y < yCenter);

      const quad3 = closestLabelsToCenter.filter(label => label.x < xCenter && label.y > yCenter);
      const quad4 = closestLabelsToCenter.filter(label => label.x < xCenter && label.y < yCenter);

      this.removeNearestLabels(quad1);
      this.removeNearestLabels(quad2);
      this.removeNearestLabels(quad3);
      this.removeNearestLabels(quad4);
    }
  }

  getClosetLayers(
    map: Map,
    refLatLog: LatLng = this.visibleLabels[0].getLatLng(),
    limit: number = this.visibleLabels.length,
    labels: GTCxLabel[] = this.visibleLabels
  ): GTCxLabel[] {
    return this.nClosestLayers(map, labels, refLatLog, limit)?.map(label => {
      label.layer.distance = label.distance;
      label.layer.x = map.project((label.layer as GTCxLabel).getLatLng() as LatLng).x;
      label.layer.y = map.project((label.layer as GTCxLabel).getLatLng() as LatLng).y;
      return label.layer;
    });
  }

  removeNearestLabels(labels: GTCxLabel[] = this.visibleLabels) {
    labels.forEach((label, index) => {
      const next = labels[index + 1];
      if (next) {
        const diff = Math.abs(label.distance - next.distance);

        if (diff < this.QUADRANT_PIXEL_DISTANCE) {
          this.zoneFeatureGroup.removeLayer(label);
        } else {
          this.zoneFeatureGroup.addLayer(label);
        }
      }
    });
  }

  showNLabels(numZones: number, labels: GTCxLabel[] = this.visibleLabels) {
    if (labels) {
      labels.forEach((label, index) => {
        if (index < numZones) {
          this.zoneFeatureGroup.addLayer(label);
        } else {
          this.zoneFeatureGroup.removeLayer(label);
        }
      });
    }
  }

  getCurrentMapBounds(map: Map): LatLngBounds {
    return map.getBounds();
  }

  onMapMoveEvent(map: Map, eventName: string) {
    map.on(eventName, event => {
      this.setLabelCollision(this.getCurrentMapBounds(map), event.target._zoom as number, map);
    });
  }

  // RFC for geojson actually sets coordinates to long/lat instead of lat long. The Leaflet libraries do not automatically reverse this to be the correct lat/long so we have to manually reverse the coordinates ourselves. Yes, it sucks as much as this sounds.
  // See https://www.rfc-editor.org/rfc/rfc7946.txt:  "A position is an array of numbers.  There MUST be two or more elements.  The first two elements are longitude and latitude...""
  reverseLongLats(feature) {
    let reversedLatLng = [];

    if (feature.geometry.type === GeometryTypes.POINT) {
      reversedLatLng = [feature.geometry.coordinates[1], feature.geometry.coordinates[0]];
    } else {
      feature.geometry.coordinates.forEach(coord => {
        if (feature.geometry.type === GeometryTypes.MULTIPOLYGON) {
          coord.forEach(innerCoord => {
            innerCoord.forEach(ic => {
              const flipped = [ic[1], ic[0]];
              reversedLatLng.push(flipped);
            });
          });
        } else if (feature.geometry.type === GeometryTypes.LINESTRING) {
          reversedLatLng.push([coord[1], coord[0]]);
        }
        // regular basic Polygon (includes circles and rectangles)
        else {
          coord.forEach(c => {
            const flipped = [c[1], c[0]];
            reversedLatLng.push(flipped);
          });
        }
      });
    }

    return {
      ...feature,
      geometry: {
        ...feature.geometry,
        coordinates: reversedLatLng
      }
    };
  }

  displayConsoleLog(logMsg: string) {
    // console.log(logMsg);
    this.datadogService.log(logMsg);
  }
}
